import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "button",
    "dropdown",
    "spreadInput",
    "premiumInput",
    "faceValueInput",
    "swapInput",
    "applyButton",
    "warning",
    "warningText",
    "success",
    "successText"
  ]
  static values = {
    spreadMin: Number,
    spreadMax: Number,
    spreadType: String,
    premiumMin: Number,
    premiumMax: Number
  }

  connect() {
    // Close dropdown when clicking outside
    this.outsideClickHandler = this.handleOutsideClick.bind(this)
    document.addEventListener('click', this.outsideClickHandler)
  }

  disconnect() {
    document.removeEventListener('click', this.outsideClickHandler)
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.closeDropdown()
    }
  }

  toggleDropdown(event) {
    event.stopPropagation()

    if (this.dropdownTarget.classList.contains('hidden')) {
      this.openDropdown()
    } else {
      this.closeDropdown()
    }
  }

  openDropdown() {
    this.dropdownTarget.classList.remove('hidden')
    this.buttonTarget.setAttribute('aria-expanded', 'true')
  }

  closeDropdown() {
    this.dropdownTarget.classList.add('hidden')
    this.buttonTarget.setAttribute('aria-expanded', 'false')
  }

  // Format face value input with commas
  formatFaceValue(event) {
    let value = event.target.value.replace(/[^\d]/g, '')
    if (value) {
      value = parseInt(value).toLocaleString()
    }
    event.target.value = value
  }

  // Handle spread input - clear premium when spread is typed
  handleSpreadInput(event) {
    if (this.hasPremiumInputTarget && event.target.value.trim() !== '') {
      this.premiumInputTarget.value = ''
    }
  }

  // Handle premium input - clear spread when premium is typed
  handlePremiumInput(event) {
    if (this.hasSpreadInputTarget && event.target.value.trim() !== '') {
      this.spreadInputTarget.value = ''
    }
  }

  updateApplyButtonState() {
    const hasFaceValue = this.faceValueInputTarget.value.replace(/,/g, '').trim() !== ''
    const hasSwap = this.hasSwapInputTarget && this.swapInputTarget.value.trim() !== ''
    const hasSpread = this.spreadInputTarget.value.trim() !== ''
    const hasPremium = this.hasPremiumInputTarget && this.premiumInputTarget.value.trim() !== ''

    if (this.hasApplyButtonTarget) {
      this.applyButtonTarget.disabled = !hasFaceValue && !hasSwap && !hasSpread && !hasPremium
    }
  }

  applyAll(event) {
    event.preventDefault()
    event.stopPropagation()

    this.hideMessages()

    const faceValueStr = this.faceValueInputTarget.value.replace(/,/g, '').trim()
    const swapStr = this.hasSwapInputTarget ? this.swapInputTarget.value.trim() : ''
    const spreadStr = this.spreadInputTarget.value.trim()
    const premiumStr = this.hasPremiumInputTarget ? this.premiumInputTarget.value.trim() : ''

    let errors = []
    let warnings = []
    let successMessages = []

    // Apply face value if provided
    if (faceValueStr !== '') {
      const faceValue = parseInt(faceValueStr)
      if (isNaN(faceValue) || faceValue <= 0) {
        errors.push("Invalid face value")
      } else {
        const result = this.applyFaceValueToAllLineItems(faceValue)
        if (result.success) {
          successMessages.push(result.message)
          // Add validation warnings if any exist
          if (result.warnings && result.warnings.length > 0) {
            warnings.push(...result.warnings)
          }
        } else {
          errors.push(result.message)
        }
      }
    }

    // Apply swap rate if provided
    if (swapStr !== '') {
      const swapValue = parseFloat(swapStr)
      if (isNaN(swapValue)) {
        errors.push("Invalid swap rate value")
      } else {
        const result = this.applySwapRateToAllLineItems(swapValue)
        if (result.success) {
          successMessages.push(result.message)
        } else {
          errors.push(result.message)
        }
      }
    }

    // Apply premium if provided (capital-price-premium mode only)
    if (premiumStr !== '' && this.spreadTypeValue === 'capital-price-premium') {
      const premiumValue = parseFloat(premiumStr)
      if (isNaN(premiumValue)) {
        errors.push("Invalid premium value")
      } else {
        // Round to 3 decimal places to avoid floating point precision issues
        const roundedPremiumAbs = Math.round(Math.abs(premiumValue) * 1000) / 1000
        const roundedMinPremium = this.hasPremiumMinValue ? Math.round(this.premiumMinValue * 1000) / 1000 : null
        const roundedMaxPremium = this.hasPremiumMaxValue ? Math.round(this.premiumMaxValue * 1000) / 1000 : null

        if (roundedMinPremium !== null && roundedPremiumAbs < roundedMinPremium) {
          errors.push(`Premium magnitude must be at least $${this.premiumMinValue}`)
        } else if (roundedMaxPremium !== null && roundedPremiumAbs > roundedMaxPremium) {
          errors.push(`Premium magnitude cannot exceed $${this.premiumMaxValue}`)
        } else {
          const result = this.applyPremiumToAllLineItems(premiumValue)
          if (result.success) {
            successMessages.push(result.message)
          } else {
            errors.push(result.message)
          }
        }
      }
    }

    // Apply spread if provided (and premium wasn't provided)
    if (spreadStr !== '' && premiumStr === '') {
      const spreadValue = parseFloat(spreadStr)
      if (isNaN(spreadValue)) {
        errors.push("Invalid spread value")
      } else if (this.spreadTypeValue !== 'capital-price-premium') {
        // Only validate spread limits for margin-spread mode
        // In capital-price-premium mode, the limits are for premium, not spread
        // Round to 3 decimal places to avoid floating point precision issues
        const roundedSpread = Math.round(spreadValue * 1000) / 1000
        const roundedMinSpread = this.hasSpreadMinValue ? Math.round(this.spreadMinValue * 1000) / 1000 : null
        const roundedMaxSpread = this.hasSpreadMaxValue ? Math.round(this.spreadMaxValue * 1000) / 1000 : null

        if (roundedMinSpread !== null && roundedSpread < roundedMinSpread) {
          errors.push(`Spread must be at least ${this.spreadMinValue}%`)
        } else if (roundedMaxSpread !== null && roundedSpread > roundedMaxSpread) {
          errors.push(`Spread cannot exceed ${this.spreadMaxValue}%`)
        } else {
          const result = this.applySpreadToAllLineItems(spreadValue)
          if (result.success) {
            successMessages.push(result.message)
          } else {
            errors.push(result.message)
          }
        }
      } else {
        // In capital-price-premium mode, apply without validating against premium limits
        const result = this.applySpreadToAllLineItems(spreadValue)
        if (result.success) {
          successMessages.push(result.message)
        } else {
          errors.push(result.message)
        }
      }
    }

    // Show feedback
    if (errors.length > 0) {
      this.showWarning(errors.join("\n"))
    } else if (successMessages.length > 0) {
      this.hideMessages()

      // Show warnings if any validation issues were found
      if (warnings.length > 0) {
        this.warningTextTarget.textContent = warnings.join("\n")
        this.warningTarget.classList.remove("hidden")
      }

      // Show success message
      this.successTextTarget.textContent = successMessages.join("\n")
      this.successTarget.classList.remove("hidden")

      // Auto-hide success after 3 seconds
      setTimeout(() => {
        this.successTarget.classList.add("hidden")
      }, 3000)

      // Clear inputs after successful application
      this.faceValueInputTarget.value = ''
      if (this.hasSwapInputTarget) {
        this.swapInputTarget.value = ''
      }
      this.spreadInputTarget.value = ''
      if (this.hasPremiumInputTarget) {
        this.premiumInputTarget.value = ''
      }
      this.updateApplyButtonState()
    }
  }

  applySpreadToAllLineItems(spreadValue) {

    // Find all quote line items
    const lineItems = document.querySelectorAll('.quote-line-item:not([style*="display: none"])')

    if (lineItems.length === 0) {
      this.showWarning("No securities found to apply spread to")
      return { success: false, message: "No securities found" }
    }

    let appliedCount = 0
    let skippedCount = 0

    lineItems.forEach((lineItem, index) => {
      // For capital-price-premium mode, we just apply the spread directly
      // without needing base margin data
      const commissionType = this.spreadTypeValue || 'margin-spread'

      // Get inventory margin for this line item
      const inventoryMarginField = lineItem.querySelector('input[name*="[inventory_margin]"]')
      const marketMarginField = lineItem.querySelector('input[name*="[market_margin]"]')

      // Use inventory margin if available, otherwise fall back to market margin
      let baseMargin = null
      if (inventoryMarginField && inventoryMarginField.value && inventoryMarginField.value !== '') {
        // Inventory margin is stored as decimal, convert to percentage for calculations
        baseMargin = parseFloat(inventoryMarginField.value)
      } else if (marketMarginField && marketMarginField.value && marketMarginField.value !== '') {
        // Market margin is also stored as decimal
        baseMargin = parseFloat(marketMarginField.value)
      }

      // Skip if we don't have a base margin to work with
      if (baseMargin === null || isNaN(baseMargin)) {
        skippedCount++
        return
      }

      const transactionType = lineItem.querySelector('select[name*="[transaction_type]"]')?.value || 'buy'

      // Calculate target margin based on spread and transaction type
      // Both baseMargin and spread need to be in the same format (decimal)
      const spreadDecimal = spreadValue / 100  // Convert percentage to decimal
      let targetMargin
      if (baseMargin !== null && !isNaN(baseMargin)) {
        if (transactionType === 'buy') {
          targetMargin = baseMargin - spreadDecimal  // Buy: margin = base - spread
        } else {
          targetMargin = baseMargin + spreadDecimal  // Sell: margin = base + spread
        }
      }

      // Find the margin input and spread input
      const marginInput = lineItem.querySelector('.margin-input')
      const spreadInput = lineItem.querySelector('.spread-input')

      if (marginInput && spreadInput) {
        // Set the spread value (keep as percentage)
        spreadInput.value = spreadValue.toFixed(4)
        spreadInput.dataset.cleared = 'true'

        // Only set margin if we calculated it (convert from decimal to percentage for display)
        if (targetMargin !== undefined) {
          marginInput.value = (targetMargin * 100).toFixed(4)
          marginInput.dataset.cleared = 'true'
        }

        // Clear price and yield fields
        const priceInput = lineItem.querySelector('.price-input')
        const yieldInput = lineItem.querySelector('.yield-input')
        if (priceInput) {
          priceInput.value = ''
          priceInput.removeAttribute('data-cleared')
        }
        if (yieldInput) {
          yieldInput.value = ''
          yieldInput.removeAttribute('data-cleared')
        }

        // Trigger input event to update UI state
        if (targetMargin !== undefined) {
          marginInput.dispatchEvent(new Event('input', { bubbles: true }))
        }
        spreadInput.dispatchEvent(new Event('input', { bubbles: true }))

        // Trigger blur on SPREAD field to recalculate margin from spread (and then price/yield)
        // This keeps the spread fixed and recalculates everything else
        spreadInput.dispatchEvent(new Event('blur', { bubbles: true }))

        appliedCount++
      }
    })

    // Return result
    if (appliedCount > 0) {
      const unit = this.getSpreadUnit()
      const message = skippedCount > 0
        ? `Spread ${spreadValue.toFixed(2)}${unit} applied to ${appliedCount} securities`
        : `Spread ${spreadValue.toFixed(2)}${unit} applied to all securities`
      return { success: true, message }
    } else {
      return { success: false, message: "No securities have margin data" }
    }
  }

  applyPremiumToAllLineItems(premiumValue) {
    // Find all quote line items
    const lineItems = document.querySelectorAll('.quote-line-item:not([style*="display: none"])')

    if (lineItems.length === 0) {
      return { success: false, message: "No securities found" }
    }

    let appliedCount = 0
    let skippedCount = 0

    lineItems.forEach((lineItem) => {
      // Find the premium input field
      const premiumInput = lineItem.querySelector('.premium-input')

      if (premiumInput) {
        // Set the premium value
        premiumInput.value = premiumValue.toFixed(3)
        premiumInput.dataset.cleared = 'true'

        // Clear spread since premium and spread are mutually exclusive
        const spreadInput = lineItem.querySelector('.spread-input')
        if (spreadInput) {
          spreadInput.value = ''
          spreadInput.removeAttribute('data-cleared')
        }

        // Trigger blur event on premium field to update capital price
        premiumInput.dispatchEvent(new Event('input', { bubbles: true }))
        premiumInput.dispatchEvent(new Event('blur', { bubbles: true }))

        appliedCount++
      } else {
        skippedCount++
      }
    })

    // Return result
    if (appliedCount > 0) {
      const message = skippedCount > 0
        ? `Premium $${premiumValue.toFixed(2)} applied to ${appliedCount} securities`
        : `Premium $${premiumValue.toFixed(2)} applied to all securities`
      return { success: true, message }
    } else {
      return { success: false, message: "Could not apply premium to any securities" }
    }
  }

  applySwapRateToAllLineItems(swapValue) {
    // Find all quote line items
    const lineItems = document.querySelectorAll('.quote-line-item:not([style*="display: none"])')

    if (lineItems.length === 0) {
      return { success: false, message: "No securities found" }
    }

    let appliedCount = 0
    let skippedCount = 0

    lineItems.forEach((lineItem) => {
      // Check if extra info is expanded, if not expand it temporarily
      const extraInfo = lineItem.querySelector('.extra-info')
      const wasHidden = extraInfo && extraInfo.classList.contains('hidden')

      if (wasHidden) {
        // Temporarily show to ensure the input is accessible
        extraInfo.classList.remove('hidden')
      }

      // Find the swap rate input field
      const swapRateInput = lineItem.querySelector('.swap-rate-input, input[name*="[swap_rate]"]')

      if (swapRateInput) {
        // Set the swap rate value
        swapRateInput.value = swapValue.toFixed(3)

        // Mark as user-changed to trigger recalculation on blur
        swapRateInput.dataset.userChanged = 'true'

        // Trigger input and blur events to update calculations
        swapRateInput.dispatchEvent(new Event('input', { bubbles: true }))
        swapRateInput.dispatchEvent(new Event('blur', { bubbles: true }))

        appliedCount++
      } else {
        skippedCount++
      }

      // Hide extra info again if it was hidden
      if (wasHidden && extraInfo) {
        extraInfo.classList.add('hidden')
      }
    })

    // Return result
    if (appliedCount > 0) {
      const message = skippedCount > 0
        ? `Swap rate ${swapValue.toFixed(3)} applied to ${appliedCount} securities`
        : `Swap rate ${swapValue.toFixed(3)} applied to all securities`
      return { success: true, message }
    } else {
      return { success: false, message: "Could not apply swap rate to any securities" }
    }
  }

  applyFaceValueToAllLineItems(faceValue) {

    // Find all quote line items
    const lineItems = document.querySelectorAll('.quote-line-item:not([style*="display: none"])')

    if (lineItems.length === 0) {
      return { success: false, message: "No securities found to apply face value to", warnings: [] }
    }

    let appliedCount = 0
    let warningsByIsin = {} // Group warnings by ISIN

    lineItems.forEach((lineItem, index) => {
      // Check if extra info is expanded, if not expand it temporarily
      const extraInfo = lineItem.querySelector('.extra-info')
      const wasHidden = extraInfo && extraInfo.classList.contains('hidden')

      if (wasHidden) {
        // Temporarily show to ensure the input is accessible
        extraInfo.classList.remove('hidden')
      }

      // Find the face value input (quantity field)
      const faceValueInput = lineItem.querySelector('.face-value-input, input[name*="[quantity]"]')

      if (faceValueInput) {
        // Check minimum denomination if available
        const minDenomination = parseInt(lineItem.dataset.minimumDenomination)
        const minParcelSize = parseInt(lineItem.dataset.minimumParcelSize)
        const securityIsin = lineItem.dataset.securityIsin

        // Validate against minimum denomination
        if (minDenomination && faceValue % minDenomination !== 0) {
          if (!warningsByIsin[securityIsin]) warningsByIsin[securityIsin] = []
          warningsByIsin[securityIsin].push(`Must be multiple of $${minDenomination.toLocaleString()}`)
        }

        // Validate against minimum parcel size
        if (minParcelSize && faceValue < minParcelSize) {
          if (!warningsByIsin[securityIsin]) warningsByIsin[securityIsin] = []
          warningsByIsin[securityIsin].push(`Below minimum of $${minParcelSize.toLocaleString()}`)
        }

        // Set the value WITHOUT commas for the input field
        faceValueInput.value = faceValue

        // Trigger events to update calculations
        // Need to dispatch 'input', 'change', and 'blur' to fully mimic manual input
        // 'change' event is critical for triggering updatePricingDisplay in quote_securities_controller
        faceValueInput.dispatchEvent(new Event('input', { bubbles: true }))
        faceValueInput.dispatchEvent(new Event('change', { bubbles: true }))
        faceValueInput.dispatchEvent(new Event('blur', { bubbles: true }))

        appliedCount++
      }

      // Hide extra info again if it was hidden
      if (wasHidden && extraInfo) {
        extraInfo.classList.add('hidden')
      }
    })

    // Trigger quote securities controller to update counts and calculations
    if (appliedCount > 0) {
      const quoteSecuritiesElement = document.querySelector('[data-controller*="oms--quote-securities"]')
      if (quoteSecuritiesElement) {
        const quoteSecuritiesController = this.application.getControllerForElementAndIdentifier(
          quoteSecuritiesElement,
          'oms--quote-securities'
        )
        if (quoteSecuritiesController && typeof quoteSecuritiesController.updateCounts === 'function') {
          quoteSecuritiesController.updateCounts()
        }
      }

      let message = `Face value $${faceValue.toLocaleString()} applied to all securities.`
      const processedWarnings = this.processWarnings(warningsByIsin)
      return { success: true, message, warnings: processedWarnings }
    } else {
      return { success: false, message: "Could not apply face value to any securities" }
    }
  }

  processWarnings(warningsByIsin) {
    const isins = Object.keys(warningsByIsin)

    if (isins.length === 0) {
      return []
    }

    // Calculate total warnings across all ISINs
    let totalWarnings = 0
    isins.forEach(isin => {
      totalWarnings += warningsByIsin[isin].length
    })

    // If more than 2 warnings total, reduce to first warning per security
    let shouldReduceToOne = totalWarnings > 2

    // Build warning messages
    let warningMessages = []
    isins.forEach(isin => {
      const messages = warningsByIsin[isin]
      if (shouldReduceToOne) {
        // Only show first warning for this ISIN
        warningMessages.push(`${isin}: ${messages[0]}`)
      } else {
        // Show all warnings for this ISIN
        messages.forEach(msg => {
          warningMessages.push(`${isin}: ${msg}`)
        })
      }
    })

    // If still more than 3 warnings, show summary instead
    if (warningMessages.length > 3) {
      const isinList = isins.join(', ')
      return [`The following securities could not be updated. Expand their details to see the errors: ${isinList}`]
    }

    return warningMessages
  }

  showWarning(message) {
    this.hideMessages()
    this.warningTextTarget.textContent = message
    this.warningTarget.classList.remove("hidden")
  }

  showSuccess(message) {
    this.hideMessages()
    this.successTextTarget.textContent = message
    this.successTarget.classList.remove("hidden")

    // Auto-hide after 3 seconds
    setTimeout(() => {
      this.successTarget.classList.add("hidden")
    }, 3000)
  }

  hideMessages() {
    this.warningTarget.classList.add("hidden")
    this.successTarget.classList.add("hidden")
  }

  getSpreadUnit() {
    // Always return % since bulk actions only apply margin spread, not premium
    return '%'
  }
}